import { useMutation } from '@apollo/client';
import { useEffect, useCallback } from 'react';
import { loadAdyenScript } from '../../../lib/loadScript';
import { encryptCard } from '../../../lib/encryptCard';
import { COUNTRIES } from '../../../constants';
import { BillingAddress } from '../usePaymentMethodsQuery';
import { usePsd2 } from '../usePsd2';
import { UPDATE_PAYMENT_METHOD_MUTATION } from './updatePaymentMethodMutation.rest';

type UpdateArgs = {
  id: string;
  cardholderName: string;
  expirationMonth: string;
  expirationYear: string;
  securityCode: string;
  billingAddress: BillingAddress;
};

export const useUpdatePaymentMethodMutation = () => {
  const { psd2Params, verifyPsd2 } = usePsd2();

  const [mutate, { loading, error }] = useMutation(UPDATE_PAYMENT_METHOD_MUTATION);

  useEffect(() => {
    loadAdyenScript();
  }, []);

  const handleUpdate = useCallback(
    async ({
      id,
      cardholderName,
      expirationMonth,
      expirationYear,
      securityCode,
      billingAddress
    }: UpdateArgs) => {
      const country = COUNTRIES.find(country => country.alpha2Code === billingAddress.countryCode);
      const encryptedCard = encryptCard({
        holderName: cardholderName,
        expiryMonth: expirationMonth,
        expiryYear: expirationYear,
        cvc: securityCode
      });

      if (!country) throw new Error('updatePaymentMethodMutation: country not found');

      const result = await mutate({
        variables: {
          id,
          input: {
            data: {
              type: 'payments',
              attributes: {
                ...psd2Params,
                encrypted_data: encryptedCard,
                billing_address: {
                  id: billingAddress.id,
                  country_name: country.name,
                  extended_address: billingAddress.extendedAddress,
                  locality: billingAddress.locality,
                  postal_code: billingAddress.postalCode,
                  region: billingAddress.region,
                  street_address: billingAddress.streetAddress
                }
              }
            }
          }
        }
      });

      const attributes = result.data?.updatePaymentMethod.data.attributes;
      await verifyPsd2(attributes?.resultCode, attributes?.psd2AuthData);

      return result;
    },
    [mutate, psd2Params, verifyPsd2]
  );

  return [handleUpdate, { loading, error }] as const;
};
